import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import CarForm from '@/components/oil-finder/CarForm';
import Footer from '@/components/layout/Footer';
import { useToast } from '@/hooks/use-toast';
import OilRecommendationsSection from '@/components/oil-finder/OilRecommendationsSection';
import OilInformationSection from '@/components/oil-finder/OilInformationSection';
import OilFaqSection from '@/components/oil-finder/OilFaqSection';
import OilCtaSection from '@/components/oil-finder/OilCtaSection';
import { generateRecommendations } from '@/utils/oilRecommendationUtils';

interface OilRecommendation {
  oilType: string;
  viscosity: string;
  description: string;
  price: string;
  image: string;
  benefits: string[];
  volume: string; // Ensuring volume is a required field
}

const OilFinder = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [recommendations, setRecommendations] = useState<OilRecommendation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [vehicleData, setVehicleData] = useState<any>(null);
  const { toast } = useToast();

  // Add new required liters state to pass to RecommendationsSection
  const [litersNeeded, setLitersNeeded] = useState<number>(4);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const handleFormSubmit = (data: any) => {
    console.log("Form submitted with data:", data);
    setVehicleData(data);
    setLitersNeeded(Number(data.litersNeeded) || 4); // Save the litersNeeded value
    setFormSubmitted(false); // Reset first
    setIsLoading(true);
    
    // Simulate API call with generated recommendations
    setTimeout(() => {
      setIsLoading(false);
      setFormSubmitted(true);
      toast({
        title: "Analysis complete",
        description: `We've found the perfect oil types for your ${data.year} ${data.make} ${data.model}`,
      });
      
      // Get recommendations with volume information
      const recs = generateRecommendations(data);
      
      // Ensure all recommendations have volume information
      const recsWithVolume = recs.map(rec => ({
        ...rec,
        volume: rec.volume || "1L, 4L, 5L" // Default volume if not specified
      }));
      
      setRecommendations(recsWithVolume);
    }, 1500);
  };

  return (
    <div className={`min-h-screen bg-white ${isLoaded ? 'animate-fade-in' : 'opacity-0'}`}>
      <Navbar />
      
      {/* Page Header */}
      <div className="pt-24 pb-12 bg-white">
        <div className="page-container">
          <div className="flex flex-col items-start text-autocare-blue">
            <div className="flex items-center text-sm mb-3">
              <Link to="/" className="hover:underline">Home</Link>
              <ChevronRight className="h-4 w-4 mx-1" />
              <span>Oil Finder</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Find the Perfect Oil for Your Vehicle</h1>
            <p className="text-gray-600 max-w-xl">
              Enter your vehicle details below and we'll recommend the right oil type and filter for optimal performance.
            </p>
          </div>
        </div>
      </div>
      
      {/* Car Form Section */}
      <section className="section-padding -mt-8">
        <div className="page-container">
          <CarForm onSubmit={handleFormSubmit} />
        </div>
      </section>
      
      {/* Loading indicator */}
      {isLoading && (
        <section className="section-padding bg-white animate-fade-in">
          <div className="page-container">
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 border-4 border-autocare-blue-light border-t-autocare-yellow rounded-full animate-spin mb-4"></div>
              <p className="text-lg font-medium text-autocare-blue">Analyzing your vehicle specifications...</p>
              <p className="text-gray-500 text-sm mt-2">Finding the best oil recommendations</p>
            </div>
          </div>
        </section>
      )}
      
      {/* Results Section */}
      <OilRecommendationsSection 
        recommendations={recommendations} 
        vehicleData={vehicleData} 
        formSubmitted={formSubmitted} 
        litersNeeded={litersNeeded}
      />
      
      {/* Information Section */}
      <OilInformationSection />
      
      {/* FAQ Section */}
      <OilFaqSection />
      
      {/* CTA Section */}
      <OilCtaSection />
      
      <Footer />
    </div>
  );
};

export default OilFinder;
